// @ts-check
import { ToastQueue } from '@adobe/react-spectrum';
import { ON_IMSLIB_INSTANCE } from '@identity/imslib/constants/ImsConstants';
import { useBreakpoint } from '@react-spectrum/utils';
import { useEffect } from 'react';

import { addCSS, addScript } from '../utils';

const insertUnavScripts = () => {
  const unavPath = process.env.REACT_APP_UNAV_PATH;
  const unavVersion = '1.5';
  return Promise.all([
    addScript([unavPath, unavVersion, 'UniversalNav.js'].join('/')),
    addCSS([unavPath, unavVersion, 'UniversalNav.css'].join('/')),
  ]);
};

/**
 * @type {UniversalNavConfig['analyticsContext']}
 */
const analyticsContext = {
  consumer: {
    name: 'exchange',
    version: [
      `${process.env.npm_package_version}`,
      process.env.NODE_ENV === 'production' ? '' : `-${process.env.NODE_ENV}`,
    ].join(''),
    platform: 'Web',
    device: 'na',
    os_version: 'na',
  },
  event: {
    visitor_guid: '',
  },
};

/**
 * @type {UniversalNavConfig}
 */
const config = {
  target: null,
  env: /** @type {UniversalNavConfig['env']} */ (process.env.REACT_APP_UNAV_ENV),
  analyticsContext,
  theme: 'light',
  locale: 'en_US',
  imsClientId: process.env.REACT_APP_CLIENT_ID,
  children: [
    {
      name: 'app-switcher',
      attributes: {},
    },
    {
      name: 'profile',
      attributes: {
        accountMenuContext: {
          aupContext: {
            releaseName: 'stable',
            showToast: (message, attributes, closeCallback) => {
              const { variant, timeout } = attributes || {};

              const toastOptions = {
                timeout: timeout || undefined,
              };

              switch (variant) {
                case 'positive':
                  ToastQueue.positive(message, toastOptions);
                  break;
                case 'negative':
                  ToastQueue.negative(message, toastOptions);
                  break;
                default:
                  ToastQueue.info(message, toastOptions);
                  break;
              }
            },
            showDialog: (element, attributes, closeCallback) => {
              const dialog = document.createElement('dialog');
              dialog.appendChild(element);
              document.body.appendChild(dialog);
              dialog.showModal();
              dialog.addEventListener('close', () => {
                closeCallback({ type: 'success' });
              });
            },
          },
          messageEventListener: (event) => {
            const { name, payload, executeDefaultAction, trackCancelIntent, trackCompleteAction } = event.detail;
            if (payload.subType === 'SignOut' && name === 'System') {
              localStorage.removeItem('orgs');
              localStorage.removeItem('selectedOrg');
              executeDefaultAction();
            }
          },
        },
        callbacks: {
          onSignIn: () => {
            window.adobeIMS.signIn();
          },
        },
        // refer to this section for more details: https://wiki.corp.adobe.com/display/CCHome/Universal+Nav+Integration+Guidelines#UniversalNavIntegrationGuidelines-ProfileConfiguration
      },
    },
  ],
};

/**
 * @typedef {import('@identity/imslib').AdobeIMS} AdobeIMS
 *
 * This hook is used to inject and initialize the UniversalNav component
 * See more at: https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=CCHome&title=Universal+Nav+Integration+Guidelines
 */
export const useUniversalNav = () => {
  const { matchedBreakpoints } = useBreakpoint();
  const isSmallerScreen = !matchedBreakpoints.includes('S');

  // Reload the UniversalNav when the screen size changes so it can show the tray experience
  useEffect(() => {
    // Do not reload if it's not even loaded xD
    if (!window.UniversalNav) {
      return;
    }

    config.showTrayExperience = isSmallerScreen;
    window.UniversalNav.reload(config);
  }, [isSmallerScreen]);

  // Add a listener to the AdobeIMS instance so we can load the UniversalNav after it's initialized
  useEffect(() => {
    // If UniversalNav is already loaded, do not add another listener when the screen size changes
    if (window.UniversalNav) {
      return;
    }

    window.addEventListener(
      ON_IMSLIB_INSTANCE,
      async (/** @type {CustomEvent<{instance: AdobeIMS; clientId: string}>} */ event) => {
        const ims = event?.detail?.instance;
        if (ims && !window.UniversalNav) {
          await insertUnavScripts();

          if (ims.isSignedInUser()) {
            config.analyticsContext.event.visitor_guid = (await ims.getProfile()).userId;
          }

          config.target = document.getElementById('nav-adobe-profile');
          config.showTrayExperience = isSmallerScreen;

          window.UniversalNav(config);
        }
      }
    );
  }, [isSmallerScreen]);
};

export default useUniversalNav;
